import { CAPTCHA_URL } from '@hunk/shared/api/apiAuth';
import { useEffect, useRef, useState } from 'react';
import type {} from 'altcha/types/react';

/**
 * "I'm not a robot" (ALTCHA, self-hosted proof-of-work - no third party, no keys). The widget fetches a challenge from
 * the server and solves it in the browser (starts when the form is focused); onChange gets the payload to send with
 * the request, or '' while it is not solved / expired. The widget code is loaded only here (login page).
 */
export function Captcha({ onChange }: { onChange: (payload: string) => void }) {
  const ref = useRef<HTMLElement>(null);
  const [loaded, setLoaded] = useState(false);
  const changed = useRef(onChange);
  changed.current = onChange;

  useEffect(() => {
    void Promise.all([import('altcha'), import('altcha/i18n/he')]).then(() => setLoaded(true));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!loaded || !el) return;
    const onState = (e: Event) => {
      const { state, payload } = (e as CustomEvent<{ state: string; payload: string | null }>).detail;
      changed.current(state === 'verified' && payload ? payload : '');
    };
    el.addEventListener('statechange', onState);
    return () => el.removeEventListener('statechange', onState);
  }, [loaded]);

  return (
    <div className="min-h-[74px]" dir="rtl">
      {loaded && (
        <altcha-widget
          ref={ref}
          challenge={CAPTCHA_URL}
          language="he"
          auto="onfocus"
          style={{
            '--altcha-max-width': '100%',
            '--altcha-border-radius': '0.75rem',
            '--altcha-color-base': '#0a0a0a',
            '--altcha-color-base-content': '#e5e5e5',
            '--altcha-border-color': 'rgba(255,255,255,0.15)',
            '--altcha-color-primary': '#d4272e',
            '--altcha-color-primary-content': '#fff'
          }}
        />
      )}
    </div>
  );
}
